"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface Framework {
  name: string;
  description: string;
  icon?: LucideIcon;
  accent?: string;
}

export interface CapabilityOverviewSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  intro: string;
  frameworks?: Framework[];
  biasLevels?: { title: string; description: string }[];
  variantDimensions?: { label: string; description: string }[];
  localization?: string[];
  columns?: { name: string; description: string; icon: LucideIcon; accent: string }[];
  primaryAccent?: string;
  secondaryAccent?: string;
  tertiaryAccent?: string;
}

export function CapabilityOverviewSection({
  id = "capability-overview",
  eyebrow,
  title,
  intro,
  frameworks,
  biasLevels,
  variantDimensions,
  localization,
  columns,
  primaryAccent = "var(--color-cyan)",
  secondaryAccent = "var(--color-purple)",
  tertiaryAccent = "var(--color-emerald)",
}: CapabilityOverviewSectionProps) {
  return (
    <Section id={id} className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/4 right-1/3 h-80 w-80 rounded-full opacity-15 blur-[120px]"
          style={{ background: primaryAccent }}
        />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: primaryAccent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{intro}</p>
          </div>
        </Reveal>

        {frameworks && frameworks.length > 0 && (
          <Reveal delay={120}>
            <div className="mt-12">
              <div className="eyebrow-tiny mb-4" style={{ color: primaryAccent }}>
                Framework Library
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {frameworks.map((f, i) => {
                  const accents = [primaryAccent, secondaryAccent, tertiaryAccent, "var(--color-amber)", "var(--color-rose)", "var(--color-denim)"] as const;
                  const accent = f.accent ?? accents[i % accents.length]!;
                  return (
                    <div
                      key={f.name}
                      className="glass rounded-2xl p-5 h-full"
                      style={{
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                      }}
                    >
                      {f.icon && (
                        <f.icon className="h-5 w-5 mb-2" style={{ color: accent }} />
                      )}
                      <h3 className="text-sm font-bold">{f.name}</h3>
                      <p className="mt-1.5 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}

        {columns && columns.length > 0 && (
          <Reveal delay={120}>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {columns.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.name}
                    className="glass rounded-2xl p-6 h-full"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.accent} 14%, transparent)`,
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, ${c.accent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.accent} 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-5 w-5" style={{ color: c.accent }} />
                      </span>
                      <h3 className="text-base font-bold">{c.name}</h3>
                    </div>
                    <p className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{c.description}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        )}

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {biasLevels && biasLevels.length > 0 && (
            <Reveal delay={200}>
              <div
                className="glass rounded-2xl p-6 h-full"
                style={{
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${secondaryAccent} 14%, transparent)`,
                }}
              >
                <div className="eyebrow-tiny mb-4" style={{ color: secondaryAccent }}>
                  Calibration Levels
                </div>
                <div className="space-y-4">
                  {biasLevels.map((b, i) => (
                    <div key={b.title} className="flex items-start gap-3">
                      <span
                        className="grid place-items-center h-7 w-7 rounded-full text-[10px] font-bold shrink-0"
                        style={{
                          background: `color-mix(in oklch, ${secondaryAccent} 18%, transparent)`,
                          color: secondaryAccent,
                        }}
                      >
                        {i + 1}
                      </span>
                      <div>
                        <div className="font-semibold text-sm">{b.title}</div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{b.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {variantDimensions && variantDimensions.length > 0 && (
            <Reveal delay={280}>
              <div
                className="glass rounded-2xl p-6 h-full"
                style={{
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${tertiaryAccent} 12%, transparent)`,
                }}
              >
                <div className="eyebrow-tiny mb-4" style={{ color: tertiaryAccent }}>
                  Multi-Variant Dimensions
                </div>
                <ul className="space-y-3">
                  {variantDimensions.map((v) => (
                    <li key={v.label} className="flex items-start gap-2 text-sm">
                      <span
                        className="h-1.5 w-1.5 rounded-full mt-2 shrink-0"
                        style={{ background: tertiaryAccent }}
                      />
                      <div>
                        <span className="font-semibold">{v.label}:</span>{" "}
                        <span className="text-[rgb(var(--fg-muted))] leading-relaxed">{v.description}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
        </div>

        {localization && localization.length > 0 && (
          <Reveal delay={360}>
            <div
              className="mt-10 glass rounded-2xl p-7"
              style={{
                boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${primaryAccent} 12%, transparent)`,
              }}
            >
              <div className="eyebrow-tiny mb-4" style={{ color: primaryAccent }}>
                Localization Framework
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {localization.map((l) => (
                  <div key={l} className="flex items-start gap-2 text-sm">
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-current mt-2 shrink-0"
                      style={{ color: primaryAccent }}
                    />
                    <span className="text-[rgb(var(--fg-muted))] leading-relaxed">{l}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </Container>
    </Section>
  );
}
